import React, { useId, useRef, useState } from 'react';
import { API } from '../config';

const MAX_BYTES = 5 * 1024 * 1024;

function validate(v) {
  const e = {};
  if (v.name.trim().length < 2) e.name = 'Please enter your full name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = 'Please enter a valid email address.';
  if (!/^\d{10}$/.test(v.mobile.replace(/[\s-]/g, ''))) e.mobile = 'Please enter a 10-digit mobile number.';
  if (!v.file) e.file = 'Please attach your resume.';
  else if (!/\.(pdf|docx?)$/i.test(v.file.name)) e.file = 'Resume must be a PDF, DOC or DOCX file.';
  else if (v.file.size > MAX_BYTES) e.file = 'Resume must be 5 MB or smaller.';
  return e;
}

// One form for both paths. With jobId it applies for that role (POST /applicants);
// without it, it sends the resume to the general pool (POST /general-applications).
export default function ApplyForm({ jobId, jobTitle, title, submitLabel, focusOnDone = true }) {
  const uid = useId().replace(/:/g, '');
  const [v, setV] = useState({ name: '', email: '', mobile: '', file: null });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const refs = { name: useRef(), email: useRef(), mobile: useRef(), file: useRef() };
  const thanksRef = useRef();
  const set = (k) => (e) => { const val = e.target.value; setV((p) => ({ ...p, [k]: val })); setErrors((p) => ({ ...p, [k]: '' })); };

  const submit = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const found = validate(v);
    setErrors(found);
    const bad = ['name', 'email', 'mobile', 'file'].find((k) => found[k]);
    if (bad) { refs[bad].current && refs[bad].current.focus(); return; }
    setSending(true);
    try {
      const body = new FormData();
      body.append('name', v.name.trim());
      body.append('email', v.email.trim().toLowerCase());
      body.append('mobile', v.mobile.replace(/[\s-]/g, ''));
      if (jobId) body.append('jobId', jobId);
      body.append('resume', v.file);
      const res = await fetch(`${API}${jobId ? '/applicants' : '/general-applications'}`, { method: 'POST', body });
      if (res.ok) {
        setDone({ first: v.name.trim().split(/\s+/)[0], email: v.email.trim().toLowerCase() });
        if (focusOnDone) setTimeout(() => thanksRef.current && thanksRef.current.focus(), 50);
        return;
      }
      let msg = 'Something went wrong. Please try again.';
      try { const data = await res.json(); if (data && data.error) msg = data.error; } catch { /* keep the default */ }
      setFormError(msg);
    } catch {
      setFormError('We could not reach our server. Please check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="thanks" role="status" tabIndex={-1} ref={thanksRef}>
        <h4>Thank you, {done.first}.</h4>
        <p>
          {jobId
            ? <>We have your application{jobTitle ? <> for {jobTitle}</> : null}. We will write to {done.email} about next steps.</>
            : <>We have your resume. If a role opens that suits you, we will write to {done.email}.</>}
        </p>
      </div>
    );
  }

  const field = (k, label, props) => (
    <div>
      <label className="f" htmlFor={`${uid}${k}`}>{label}</label>
      <input id={`${uid}${k}`} ref={refs[k]} value={v[k]} onChange={set(k)} aria-invalid={errors[k] ? 'true' : undefined}
        aria-describedby={`${uid}${k}e`} {...props} />
      <span className="err" id={`${uid}${k}e`}>{errors[k]}</span>
    </div>
  );

  return (
    <form className="aform" onSubmit={submit} noValidate>
      {title && <h4>{title}</h4>}
      <div className="fgrid two">
        {field('name', 'Full name', { type: 'text', autoComplete: 'name' })}
        {field('email', 'Email', { type: 'email', autoComplete: 'email' })}
        {field('mobile', 'Mobile (10 digits)', { type: 'tel', inputMode: 'numeric', autoComplete: 'tel-national', maxLength: 14 })}
        <div>
          <label className="f" htmlFor={`${uid}file`}>Resume</label>
          <input id={`${uid}file`} ref={refs.file} type="file" accept=".pdf,.doc,.docx" aria-invalid={errors.file ? 'true' : undefined}
            aria-describedby={`${uid}fileh ${uid}filee`}
            onChange={(e) => { const f = e.target.files[0] || null; setV((p) => ({ ...p, file: f })); setErrors((p) => ({ ...p, file: '' })); }} />
          <span className="hint" id={`${uid}fileh`}>PDF, DOC or DOCX, up to 5 MB.</span>
          <span className="err" id={`${uid}filee`}>{errors.file}</span>
        </div>
      </div>
      {formError && <div className="err form-err" role="alert">{formError}</div>}
      <div className="cta" style={{ marginTop: 20 }}>
        <button className="btn main" type="submit" disabled={sending}>{sending ? 'Sending' : submitLabel}</button>
      </div>
      <p className="hint consent">By sending, you agree to your resume being reviewed by our HR team and AI system.</p>
    </form>
  );
}
